import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

import { trimDecimalZeros, trimLeadingZeros } from '@shared-helpers/string-number-helpers';

@Component({
  selector: 'numeric-input-field',
  templateUrl: './numeric-input-field.component.html',
  styleUrl: './numeric-input-field.component.css',
  standalone: false,
})
export class NumericInputFieldComponent implements OnInit {
  @Input() public control = new FormControl<string>('');
  @Input() public max?: number;
  @Input() public min = 0;
  @Input() public formClass = '';
  @ViewChild('inputRef') public readonly inputRef!: ElementRef<HTMLInputElement>;

  public shouldShowError = false;

  constructor(private readonly elementRef: ElementRef) {}

  public ngOnInit(): void {
    this.control.markAllAsTouched();
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    if (
      !this.elementRef.nativeElement.contains(event.target) &&
      ((this.control.disabled && this.control.errors != null) || (this.control.enabled && this.control.errors == null))
    ) {
      //clear the error box if you click away from the component. E.G. invalid activity on the day
      this.shouldShowError = false;
    }
  }

  public trimAndValidateControl(): void {
    //THIS FUNCTION IS REALLY IMPORTANT!!! IF you make changes to this function, please inform the whole team.
    //Allow decimals, but limit it to 2 decimal places
    const val = this.inputRef.nativeElement.value; //here we need to fetch the actual html value, because angular will not grab a number value like this 1.00000000
    const numValue = parseFloat(val); //convert the string to a number

    if (numValue < this.min) {
      //Set the minimum if no value is provided or the provided value is less than the minimum
      this.control.setValue(this.min.toString());
    } else if (this.max && numValue > this.max) {
      //If a max value has been set (since it is optional) AND the provided value is above that, set the max value as the value
      this.control.setValue(this.max.toString());
    } else if (/\.\d{3,}$/.test(val)) {
      //test if input is 3 or more decimals
      const regex = /^\d+(\.\d{1,2})?/; //finds the whole number and first 2 decimals
      const match = regex.exec(val);
      this.control.setValue(match ? match[0] : val); // If there's a match, return the matched part, otherwise return the original input
    }
    this.shouldShowError = this.control.errors != null;
  }

  public handleFocusChange(): void {
    //THIS FUNCTION IS REALLY IMPORTANT!!! IF you make changes to this function, please inform the whole team.
    let val = trimDecimalZeros(trimLeadingZeros(this.inputRef.nativeElement.value)); //remove any meaningless leading and following zeros: 01.10 => 1.1
    if (val === '0') val = ''; //don't show anything for meaningless values (00.00, 0, 00.0, 0.0)
    this.control.setValue(val);
  }

  public clickValidateForDisabled(): void {
    if (this.control.disabled) {
      this.control.markAllAsTouched();
      this.shouldShowError = this.control.errors != null;
    }
    this.selectAllText(); //this is here because the click event is bound to the parent function, but the child function still needs to run.
  }

  private selectAllText(): void {
    const inputElement = this.inputRef.nativeElement;
    inputElement.select(); // This selects all the text in the input element
  }
}
